/*
 * lu-cro.js: small behaviour fixes that go with assets/lu-cro.css (same proposal IDs).
 * Loaded with defer from layout/theme.liquid, after the theme scripts.
 * Theme editor: P1 is re-applied on shopify:section:load / shopify:block:select (a re-rendered section gets a
 * new Flickity instance); P3 is event delegation on document (works for re-rendered sections as they are) and
 * only runs while <body> has the class "lu-drag" (Theme settings > LU – Optimizări > Drag cu mouse-ul pe carusele).
 * V1 (gallery videos) only touches videos inside .lu-gvideo, which the Liquid outputs only when "Video în galerie" is on.
 * H1 (hero slideshow failsafe) only shows slide texts / buttons that are still hidden ~3s after load (fix: assets/slideshow.js H1).
 * PERF2-1 (lazy reels videos) only touches <video data-lu-src> inside .shoppable-video-reels--carousel.
 */
(function () {
  'use strict';

  /* P1: shoppable video reels carousel (Flickity, sections/shoppable-view-reels.liquid) never takes over a
     vertical page scroll on touch phones.
     a) Flickity starts a horizontal drag after 3px of x movement, whatever the y movement, so a slightly
        diagonal vertical swipe that starts on a reel moved the carousel (and played another video).
        Now the first 8px decide the gesture: mostly vertical = page scroll only, never a carousel drag.
     b) A tap that stops a momentum scroll was a Flickity "staticClick": the theme then selects the tapped
        (peeking) reel and starts its video, so the carousel jumped sideways under the finger. Taps within
        350ms of a page scroll are now ignored.
     c) No focus() on pointer down (Flickity focuses the 537px-tall carousel; older iOS ignores
        preventScroll and scrolls it into view). Keyboard focus via Tab still works (tabindex stays). */
  var lastScroll = 0;
  window.addEventListener('scroll', function () { lastScroll = Date.now(); }, { passive: true });

  function patchReels(el) {
    var f = window.Flickity && window.Flickity.data(el);
    if (!f) return false;
    if (f.luPatched) return true;
    f.luPatched = true;
    var lock = null;
    f.on('pointerDown', function () { lock = null; });
    f.hasDragStarted = function (v) {
      if (lock === null) {
        var ax = Math.abs(v.x), ay = Math.abs(v.y);
        if (ax < 8 && ay < 8) return false;
        lock = ax > ay * 1.2 ? 'x' : 'y';
      }
      return lock === 'x';
    };
    f.pointerDownFocus = function () {};
    var sc = f.options.on && f.options.on.staticClick;
    if (sc) {
      f.off('staticClick', sc);
      f.on('staticClick', function () {
        if (Date.now() - lastScroll < 350) return;
        sc.apply(this, arguments);
      });
    }
    return true;
  }
  /* PERF2-1: lazy reels videos (sections/shoppable-view-reels.liquid renders <video preload="none"> with the
     poster in data-lu-poster and one mp4 rendition <= 720p in data-lu-src, no src, no autoplay; before, every
     reel was a 1080p <source> with preload="metadata" and reel 1 autoplayed, so ~27 MB of video loaded with the page).
     a) Poster: set when the reel (or one of its 2 neighbours on each side) comes within 200px of the viewport.
     b) Src: set only when a reel is asked to play. video.play() is wrapped per element, so the theme's own calls
        (assets/slideshow.js videoPlay on Flickity change, the ready/loadstart hook, video-controls play button)
        load the file first and then play, unchanged.
     c) The active (selected) reel plays while the carousel is within 200px of the viewport and pauses when it
        leaves (unless the visitor paused it with the play/pause button). On every carousel change all other
        reels pause, so only one reel ever plays. */
  function rvPoster(v) {
    if (v && v.dataset.luPoster && !v.getAttribute('poster')) v.setAttribute('poster', v.dataset.luPoster);
  }
  function rvSrc(v) {
    if (v && v.dataset.luSrc && !v.getAttribute('src')) {
      v.muted = true;
      v.preload = 'auto';
      v.src = v.dataset.luSrc;
    }
  }
  function rvActive(car) {
    var f = window.Flickity && window.Flickity.data(car);
    var slide = (f && f.selectedElement) || car.querySelector('.carousel__slide');
    return slide ? slide.querySelector('video') : null;
  }
  function rvUserPaused(v) {
    var b = v.parentElement && v.parentElement.querySelector('.video-controls--play-pause');
    return !!(b && b.classList.contains('paused'));
  }
  function rvPlay(v) {
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  function rvSetup(car) {
    if (car.luRv) return;
    var list = Array.prototype.slice.call(car.querySelectorAll('video[data-lu-src]'));
    if (!list.length) return;
    car.luRv = true;
    list.forEach(function (v) {
      v.play = function () {
        rvPoster(v);
        rvSrc(v);
        return HTMLMediaElement.prototype.play.call(v);
      };
    });
    if (!('IntersectionObserver' in window)) {
      list.forEach(rvPoster);
      rvPlay(rvActive(car) || list[0]);
      return;
    }
    var pIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var i = list.indexOf(e.target), n = list.length;
        for (var k = -2; k <= 2; k++) rvPoster(list[((i + k) % n + n) % n]);
        pIO.unobserve(e.target);
      });
    }, { rootMargin: '200px' });
    list.forEach(function (v) { pIO.observe(v); });
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        car.luNear = e.isIntersecting;
        var v = rvActive(car);
        if (!v) return;
        if (e.isIntersecting) { if (v.paused && !rvUserPaused(v)) rvPlay(v); }
        else if (!v.paused) v.pause();
      });
    }, { rootMargin: '200px' }).observe(car);
  }
  function rvBind(car) {
    var f = window.Flickity && window.Flickity.data(car);
    if (!f || f.luRvBound) return;
    f.luRvBound = true;
    f.on('change', function () {
      var cur = rvActive(car);
      car.querySelectorAll('video').forEach(function (v) { if (v !== cur && !v.paused) v.pause(); });
    });
  }

  function initReels(tries) {
    var pending = 0;
    document.querySelectorAll('.shoppable-video-reels--carousel').forEach(function (el) {
      rvSetup(el);
      if (!patchReels(el)) pending++;
      else rvBind(el);
    });
    if (pending && tries < 40) setTimeout(function () { initReels(tries + 1); }, 250);
  }

  /* P3: mouse drag-to-scroll for the native horizontal scroll rows (CSS overflow-x + scroll-snap):
     homepage swipe rows (Q8), product recommendations, the product gallery below 768px, and any other
     overflow-x row. Touch and pen keep the browser's own scrolling; Flickity carousels (categories,
     reels) already handle mouse drags themselves and are skipped (their viewport is overflow: hidden).
     The row is found at pointerdown, so it keeps working after a window resize. A drag of more than
     6px cancels the click that follows it (product cards and their "Alege opțiuni" button do not open
     after a drag; a drag may start on that button). */
  function scrollRow(target) {
    for (var el = target; el && el !== document.body; el = el.parentElement) {
      if (el.scrollWidth <= el.clientWidth + 2) continue;
      var ox = getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') return el;
    }
    return null;
  }
  function snapTarget(row, dir) {
    var rr = row.getBoundingClientRect(), cs = getComputedStyle(row);
    var pad = parseFloat(cs.scrollPaddingLeft) || 0, max = row.scrollWidth - row.clientWidth;
    var best = null, bestDist = Infinity;
    var aim = row.scrollLeft + dir * Math.min(row.clientWidth * 0.15, 40);
    Array.prototype.forEach.call(row.children, function (c) {
      var r = c.getBoundingClientRect();
      if (!r.width) return;
      var align = getComputedStyle(c).scrollSnapAlign;
      var pos = align.indexOf('center') > -1
        ? row.scrollLeft + (r.left + r.width / 2) - (rr.left + row.clientWidth / 2)
        : row.scrollLeft + r.left - rr.left - pad;
      pos = Math.max(0, Math.min(max, pos));
      var d = Math.abs(pos - aim);
      if (d < bestDist) { bestDist = d; best = pos; }
    });
    return best === null ? row.scrollLeft : best;
  }
  /* html.lu-mouse while the last pointer used was a mouse (grab cursor in lu-cro.css P3) */
  function dragOn() { return !!(document.body && document.body.classList.contains('lu-drag')); }
  document.addEventListener('pointermove', function (e) {
    if (!dragOn()) return;
    document.documentElement.classList.toggle('lu-mouse', e.pointerType === 'mouse');
  }, { passive: true });
  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'mouse') document.documentElement.classList.remove('lu-mouse');
  }, { passive: true });
  var drag = null;
  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !dragOn()) return;
    if (e.target.closest('input, textarea, select, .flickity-enabled, .side-panel, .mobile-menu-drawer')) return;
    var row = scrollRow(e.target);
    if (!row) return;
    drag = { row: row, x: e.clientX, left: row.scrollLeft, moved: false, snap: row.style.scrollSnapType, behavior: row.style.scrollBehavior };
  });
  document.addEventListener('pointermove', function (e) {
    if (!drag || e.pointerType !== 'mouse') return;
    var dx = e.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      drag.row.style.scrollSnapType = 'none';
      drag.row.style.scrollBehavior = 'auto';
      document.documentElement.classList.add('lu-dragging');
    }
    drag.row.scrollLeft = drag.left - dx;
    e.preventDefault();
  });
  function endDrag(e) {
    if (!drag) return;
    var d = drag;
    drag = null;
    if (!d.moved) return;
    document.documentElement.classList.remove('lu-dragging');
    var dir = d.row.scrollLeft > d.left ? 1 : d.row.scrollLeft < d.left ? -1 : 0;
    d.row.scrollTo({ left: snapTarget(d.row, dir), behavior: 'smooth' });
    setTimeout(function () { d.row.style.scrollSnapType = d.snap; d.row.style.scrollBehavior = d.behavior; }, 450);
    if (e && e.type === 'pointerup') {
      var kill = function (ev) { ev.preventDefault(); ev.stopPropagation(); ev.stopImmediatePropagation(); };
      window.addEventListener('click', kill, { capture: true, once: true });
      setTimeout(function () { window.removeEventListener('click', kill, { capture: true }); }, 50);
    }
  }
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
  /* native image / link dragging would swallow the mouse moves inside a scroll row */
  document.addEventListener('dragstart', function (e) {
    if (dragOn() && e.target.closest && scrollRow(e.target) && !e.target.closest('.flickity-enabled')) e.preventDefault();
  });

  /* V1: product gallery videos (.lu-gvideo, snippets/product-media.liquid) play muted in a loop while at least
     half of the video is on screen, and pause when scrolled or swiped away (CPU / battery). The IntersectionObserver
     sees the clipping of the gallery's own horizontal scroll (mobile) and of Flickity viewports, so a swipe to another
     slide pauses it and swiping back resumes it. Guards: controls stay off, muted/loop/playsinline stay on, and a
     'ended' fallback restarts the clip in case a browser ignores loop. If autoplay is refused (iOS Low Power Mode),
     a tap on the video starts it. Only runs for markup rendered with the theme setting on. */
  var gvIO = null;
  function gvPlay(v) {
    if (!v.paused) return;
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  function gvSetup(v) {
    if (v.luGv) return;
    v.luGv = true;
    v.muted = true; v.defaultMuted = true; v.loop = true; v.playsInline = true; v.controls = false;
    v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('webkit-playsinline', '');
    v.removeAttribute('controls');
    v.addEventListener('ended', function () { if (v.luVisible) { v.currentTime = 0; gvPlay(v); } });
    v.addEventListener('click', function () { if (v.paused) gvPlay(v); });
    new MutationObserver(function () { if (v.controls) v.controls = false; })
      .observe(v, { attributes: true, attributeFilter: ['controls'] });
    if (gvIO) gvIO.observe(v); else gvPlay(v);
  }
  function initGalleryVideos() {
    if (!gvIO && 'IntersectionObserver' in window) {
      gvIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target;
          v.luVisible = e.isIntersecting && e.intersectionRatio >= 0.5;
          if (v.luVisible && !document.hidden) gvPlay(v); else if (!v.paused) v.pause();
        });
      }, { threshold: [0, 0.5, 1] });
      document.addEventListener('visibilitychange', function () {
        document.querySelectorAll('.lu-gvideo video').forEach(function (v) {
          if (document.hidden) { if (!v.paused) v.pause(); } else if (v.luVisible) gvPlay(v);
        });
      });
    }
    document.querySelectorAll('.lu-gvideo video').forEach(gvSetup);
  }

  /* H1: hero slideshow failsafe. The theme hides the slide subheading / heading / text / buttons until its GSAP
     timeline reveals them (.animations-true ... { visibility: hidden }, lines and buttons pushed down 100% inside
     overflow:hidden wrappers). The root cause of the iPhone "no heading, no button" bug is fixed in
     assets/slideshow.js (animateSlides no longer waits only for document.fonts.ready). This is the safety net for
     anything else that could stall the reveal (animations.min.js blocked or failing, an error in the theme JS):
     ~3s after load, on the visible slide of every .main-slideshow, any of these elements that is still hidden or
     still offset and NOT being animated right now is shown in place. A working reveal ends after ~1.5s, so this
     normally changes nothing. */
  function heroShown(el) {
    var cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    if (cs.transform && cs.transform !== 'none') {
      var m = cs.transform.match(/matrix(3d)?\(([^)]+)\)/);
      if (m) {
        var v = m[2].split(',').map(parseFloat);
        var ty = m[1] ? v[13] : v[5];
        if (Math.abs(ty) > 1) return false;
      }
    }
    return true;
  }
  function heroAnimating(el) {
    if (!window.gsap || !gsap.getTweensOf) return false;
    return gsap.getTweensOf(el).some(function (t) {
      for (var p = t; p; p = p.parent) { if (p.isActive && p.isActive()) return true; }
      return false;
    });
  }
  function heroFailsafe() {
    document.querySelectorAll('.main-slideshow').forEach(function (ss) {
      var slide = ss.querySelector('.carousel__slide.is-selected') || ss.querySelector('.carousel__slide');
      if (!slide) return;
      slide.querySelectorAll('.subheading, .split-text, .split-text .line-child > div, .button').forEach(function (el) {
        if (heroShown(el) || heroAnimating(el)) return;
        el.style.visibility = 'visible';
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    });
  }
  function initHeroFailsafe() { setTimeout(heroFailsafe, 3000); }

  /* PERF2-2: in sections/slideshow.liquid only the LCP slide loads with the page; the other slides' images are
     lazysizes data-src. Warm them up 1.5s after window load, so an autoplay / swipe change never shows an empty
     slide. Images hidden by the desktop/mobile split (display: none) are left to lazysizes. */
  function heroWarm() {
    if (!window.lazySizes || !window.lazySizes.loader) return;
    document.querySelectorAll('.main-slideshow .carousel__slide:not(:first-child) .slideshow__slide-bg img.lazyload').forEach(function (img) {
      if (img.getClientRects().length) window.lazySizes.loader.unveil(img);
    });
  }
  if (document.readyState === 'complete') setTimeout(heroWarm, 1500);
  else window.addEventListener('load', function () { setTimeout(heroWarm, 1500); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initReels(0); initGalleryVideos(); initHeroFailsafe(); });
  } else {
    initReels(0);
    initGalleryVideos();
    initHeroFailsafe();
  }

  /* Theme editor (customizer) lifecycle */
  document.addEventListener('shopify:section:load', function () { initReels(0); initGalleryVideos(); initHeroFailsafe(); });
  document.addEventListener('shopify:block:select', function () { initReels(0); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (drag && e.target.contains(drag.row)) {
      drag = null;
      document.documentElement.classList.remove('lu-dragging');
    }
  });
})();
